import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, WarningCircle, Check, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, getErrorMessage } from "@/lib/api";

const FIELDS = [
  { key: "model", label: "Model" },
  { key: "battery_configuration", label: "Battery" },
  { key: "inverter_charger", label: "Inverter / Charger" },
  { key: "solar_configuration", label: "Solar Configuration" },
  { key: "management_system", label: "Management System" },
  { key: "interior_palette", label: "Interior Palette" },
  { key: "exterior_colour", label: "Exterior Colour" },
  { key: "decal_colour", label: "Decal Colour" },
  { key: "additional_options", label: "Additional Options" },
  { key: "axle", label: "Axle" },
  { key: "water_tank_capacity", label: "Plumbing Configuration" },
];

export function ReportDiscrepancyModal({ open, onClose, caravan }) {
  const [active, setActive] = useState({});
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggle = (key) =>
    setActive((a) => {
      const n = { ...a };
      if (key in n) delete n[key];
      else n[key] = "";
      return n;
    });
  const setVal = (key, v) => setActive((a) => ({ ...a, [key]: v }));

  const close = () => {
    setActive({});
    setNote("");
    onClose();
  };

  const submit = async () => {
    const corrections = Object.entries(active)
      .filter(([, v]) => (v || "").trim())
      .map(([key, v]) => ({
        field_key: key,
        field_label: FIELDS.find((f) => f.key === key)?.label || key,
        current_value: caravan?.[key] || "",
        suggested_value: v.trim(),
      }));
    if (corrections.length === 0 && !note.trim()) {
      toast.error("Pick a field to correct or add a note describing what's wrong");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/caravan/report-discrepancy", {
        chassis_number: caravan.chassis_number,
        corrections,
        note: note.trim(),
      });
      toast.success("Thanks! We've flagged this for the OTM team to review and correct.");
      close();
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't send your report. Please try again shortly."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
          data-testid="report-discrepancy-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="relative w-full sm:max-w-xl bg-[#0F0F0F] border border-neutral-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col"
          >
            <div className="flex items-start gap-3 p-5 sm:p-6 border-b border-neutral-800">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                <WarningCircle size={20} className="text-amber-400" weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading font-semibold text-white leading-tight">Report incorrect details</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Tell us what's different for{" "}
                  <span className="font-mono text-[#73bf44]">{caravan?.chassis_number}</span> and the OTM team will review and fix it.
                </p>
              </div>
              <button onClick={close} data-testid="report-cancel" className="p-1.5 rounded-lg text-neutral-500 hover:text-white hover:bg-white/5 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto p-5 sm:p-6 space-y-2.5">
              <div className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-neutral-500 mb-1">
                Which details are wrong? (optional)
              </div>
              {FIELDS.map((f) => {
                const on = f.key in active;
                const current = (caravan?.[f.key] || "").toString();
                return (
                  <div
                    key={f.key}
                    className={`rounded-xl border transition-colors ${on ? "border-[#73bf44]/50 bg-[#73bf44]/[0.04]" : "border-neutral-800 bg-[#141414]"}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(f.key)}
                      data-testid={`report-field-toggle-${f.key}`}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    >
                      <span
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                          on ? "bg-[#73bf44] border-[#73bf44]" : "border-neutral-600"
                        }`}
                      >
                        {on && <Check size={13} className="text-[#050505]" weight="bold" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-white">{f.label}</span>
                        <span className="block text-xs text-neutral-500 truncate">
                          On file: {current || "—"}
                        </span>
                      </span>
                    </button>
                    {on && (
                      <div className="px-4 pb-3.5 pl-12">
                        <input
                          autoFocus
                          value={active[f.key]}
                          onChange={(e) => setVal(f.key, e.target.value)}
                          placeholder={`The correct ${f.label.toLowerCase()}…`}
                          data-testid={`report-field-input-${f.key}`}
                          className="w-full bg-black/50 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600"
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="pt-2">
                <div className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-neutral-500 mb-1.5">
                  Anything else? (optional)
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. My van actually has 2x 200Ah lithium batteries, not 1x 100Ah."
                  data-testid="report-note-input"
                  className="w-full bg-black/50 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-5 sm:p-6 border-t border-neutral-800">
              <button
                onClick={close}
                className="px-4 py-2.5 rounded-full text-sm text-neutral-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="report-submit"
                className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-60"
              >
                <PaperPlaneTilt size={16} weight="fill" /> {submitting ? "Sending…" : "Send report"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
